import { useRef } from "react";
import styles from "../HealthChat.module.css";
import { BotIcon, MicIcon, EndCallIcon, SendIcon } from "../icons";
import useVoiceChat from "../../../hooks/useVoiceChat";


export default function VoiceScreen({ t, lang = "en", onEnd, onSendMessage }) {
  const {
    isListening,
    liveText,
    isSpeaking,
    micError,
    toggleMic,
    stopListening,
    speak,
    stopSpeaking,
    isSpeechSupported,
  } = useVoiceChat({
    lang,
    autoSend: true,
    silenceMs: 1800,
    onSendText: (capturedText) => {
      // Pass captured text up — ChatScreen / parent handles translate + Rasa + TTS
      if (onSendMessage) onSendMessage(capturedText);
    },
  });

  const handleEnd = () => {
    stopListening();
    stopSpeaking();
    onEnd?.();
  };

  // Soundwave bar heights for animation
  const BARS = [0.4, 0.7, 1, 0.6, 0.85, 0.5, 0.9, 0.65, 0.45, 0.8];

  return (
    <div className={styles.voiceBox}>

      {/* ── Avatar with animated soundwave ring ─────────────────────── */}
      <div style={{ position: "relative", marginBottom: 8 }}>
        <div
          className={styles.voiceAvatar}
          style={
            isListening
              ? {
                  boxShadow:
                    "0 0 0 10px rgba(19,37,115,0.10), 0 0 0 22px rgba(19,37,115,0.05)",
                  transition: "box-shadow 0.3s ease",
                }
              : isSpeaking
              ? {
                  boxShadow:
                    "0 0 0 10px rgba(34,197,94,0.12), 0 0 0 22px rgba(34,197,94,0.06)",
                  transition: "box-shadow 0.3s ease",
                }
              : {}
          }
        >
          <BotIcon size={38} color="#fff" />
        </div>
      </div>

      {/* Name + badge */}
      <div className={styles.voiceName}>
        {t.name} <span className={styles.aiBadge}>AI</span>
      </div>
      <div className={styles.voiceRole}>{t.role}</div>

      {/* ── Status pill ──────────────────────────────────────────────── */}
      <div className={styles.voiceStatus}>
        <span
          className={styles.voiceDot}
          style={{
            background: isListening
              ? "#e53935"
              : isSpeaking
              ? "#22c55e"
              : "#94a3b8",
            animation: isListening || isSpeaking ? "pulseDot 1.2s ease infinite" : "none",
          }}
        />
        {isListening
          ? (t.statusListening || "Listening…")
          : isSpeaking
          ? (t.statusSpeaking || "Speaking…")
          : micError
          ? micError
          : (t.status || "Ready")}
      </div>

      {/* ── Soundwave visualiser (CSS-only, active when listening/speaking) ── */}
      {(isListening || isSpeaking) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            height: 36,
            margin: "4px 0",
          }}
        >
          {BARS.map((h, i) => (
            <div
              key={i}
              style={{
                width: 3,
                borderRadius: 4,
                background: isListening ? "#e53935" : "#22c55e",
                height: `${Math.round(h * 32)}px`,
                animation: `waveBar 0.8s ease-in-out ${(i * 0.08).toFixed(2)}s infinite alternate`,
                opacity: 0.85,
              }}
            />
          ))}
        </div>
      )}

      {/* ── Live transcript box ───────────────────────────────────────── */}
      <div
        style={{
          width: "100%",
          maxWidth: 300,
          minHeight: 64,
          background: "var(--color-background-primary)",
          border: `0.5px solid ${isListening ? "var(--primary-color)" : "var(--color-border-secondary)"}`,
          borderRadius: 14,
          padding: "10px 14px",
          fontSize: 13.5,
          lineHeight: 1.55,
          textAlign: "left",
          wordBreak: "break-word",
          transition: "border-color 0.2s",
        }}
      >
        {liveText ? (
          <span style={{ color: "var(--primary-color)", fontStyle: isListening ? "italic" : "normal" }}>
            {liveText}
          </span>
        ) : (
          <span style={{ color: "var(--color-text-secondary)", fontSize: 12.5 }}>
            {isListening
              ? (t.hintStop || "Tap mic to stop & send, or wait for auto-send…")
              : isSpeaking
              ? (t.statusSpeaking || "Speaking…")
              : (t.hintStart || "Tap the mic to start speaking")}
          </span>
        )}
      </div>

      {/* Auto-send hint */}
      {isListening && liveText && (
        <div
          style={{
            fontSize: 11,
            color: "#94a3b8",
            textAlign: "center",
            marginTop: -4,
          }}
        >
          Sending automatically after a short pause…
        </div>
      )}

      {/* Not supported */}
      {!isSpeechSupported && (
        <div style={{ fontSize: 12, color: "#e53935", textAlign: "center", maxWidth: 260 }}>
          Speech recognition is not supported in this browser. Try Chrome or Edge.
        </div>
      )}

      {/* ── Action buttons ────────────────────────────────────────────── */}
      <div className={styles.voiceActions} style={{ marginTop: 8 }}>

        {/* Mic toggle */}
        <button
          className={styles.micActionBtn}
          onClick={toggleMic}
          disabled={!isSpeechSupported}
          title={isListening ? "Stop & send" : "Start"}
          style={
            isListening
              ? {
                  background: "#fce4ec",
                  color: "#e53935",
                  border: "1.5px solid #e53935",
                  animation: "micPulse 1s ease infinite",
                }
              : {}
          }
        >
          <MicIcon size={22} />
        </button>

        {/* Stop speaking */}
        {isSpeaking && (
          <button
            onClick={stopSpeaking}
            title="Stop speaking"
            style={{
              width: 54,
              height: 54,
              borderRadius: "50%",
              background: "#eaf0ff",
              border: "1.5px solid var(--primary-color)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              color: "var(--primary-color)",
            }}
          >
            ⏹
          </button>
        )}

        {/* End call */}
        <button className={styles.endCallBtn} onClick={handleEnd} title="End call">
          <EndCallIcon size={22} />
        </button>
      </div>

      {/* ── Keyframes ─────────────────────────────────────────────────── */}
      <style>{`
        @keyframes micPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(229,57,53,0.45); }
          50%       { box-shadow: 0 0 0 8px rgba(229,57,53,0); }
        }
        @keyframes waveBar {
          from { transform: scaleY(0.35); }
          to   { transform: scaleY(1); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
